import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { ArrowRight } from 'lucide-react';
import { CreditCard } from '../types/card';
import { useAffiliateRedirect } from '@/hooks/useAffiliateRedirect';
import { trackAffiliateClick } from '@/lib/analytics';
import { trackAffiliateClickGTM } from '@/lib/gtm';

interface StickyMobileCTAProps {
  card: CreditCard;
  affiliateLink?: string;
  cardId: string;
}

export function StickyMobileCTA({ card, affiliateLink, cardId }: StickyMobileCTAProps) {
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const { handleAffiliateRedirect } = useAffiliateRedirect();

  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const currentScrollY = window.scrollY;
          
          // Show when scrolling up, hide when scrolling down
          // But always show at the very top
          if (currentScrollY < 100) {
            setIsVisible(true);
          } else if (currentScrollY < lastScrollY) {
            // Scrolling up
            setIsVisible(true);
          } else if (currentScrollY > lastScrollY + 10) {
            // Scrolling down (with small threshold to prevent flickering)
            setIsVisible(false);
          }
          
          setLastScrollY(currentScrollY);
          ticking = false;
        });
        
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  const handleClick = async () => {
    if (affiliateLink) {
      trackAffiliateClick(cardId, 'detail_page', card.name);
      
      // Wait for GTM event to be sent
      await trackAffiliateClickGTM({
        cardId: cardId,
        cardName: card.name,
        issuerName: card.bank,
        affiliateUrl: affiliateLink,
        clickSource: 'detail_page',
        annualFee: card.annualFee,
        effectiveRate: card.effectiveRate,
      });
      
      // Now navigate
      handleAffiliateRedirect({
        cardId: cardId,
        cardName: card.name,
        issuerName: card.bank,
        affiliateUrl: affiliateLink,
        source: 'detail_page',
        annualFee: card.annualFee,
        effectiveRate: card.effectiveRate,
      });
    }
  };

  // Don't render if card has no affiliate link
  if (!affiliateLink) {
    return null;
  }

  return (
    <div 
      className={`lg:hidden fixed bottom-0 left-0 right-0 z-50 transition-transform duration-300 ${
        isVisible ? 'translate-y-0' : 'translate-y-full'
      }`}
    >
      {/* Backdrop with blur */}
      <div className="absolute inset-0 bg-white/95 backdrop-blur-md border-t border-gray-200 shadow-2xl" />
      
      {/* Content */}
      <div className="relative px-4 pt-3 pb-safe">
        {/* Card info row - compact */}
        <div className="flex items-center justify-between mb-2.5">
          <div className="flex-1 min-w-0 mr-3">
            <p className="text-sm text-gray-900 truncate">{card.name}</p>
            <p className="text-xs text-gray-500">
              {card.annualFee === 0 ? (
                <span className="text-green-600">0 € Jahresgebühr</span>
              ) : (
                `${card.annualFee.toLocaleString('de-DE')} € Jahresgebühr`
              )}
              {' • '}
              {card.effectiveRate}% eff. Zins
            </p>
          </div>
          
          {/* Small logo */}
          <div className="w-12 h-8 bg-gradient-to-br from-gray-100 to-gray-50 rounded-sm overflow-hidden flex-shrink-0 border border-gray-200">
            {card.logo && (
              <img
                src={card.logo}
                alt={card.bank}
                className="w-full h-full object-cover"
              />
            )}
          </div>
        </div>

        {/* CTA Button */}
        <Button 
          size="lg"
          className="w-full gap-2 mb-1 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-lg hover:shadow-xl active:scale-[0.98] transition-all duration-200"
          onClick={handleClick}
        >
          Jetzt beantragen
          <ArrowRight className="w-4 h-4" />
        </Button>

        {/* iOS safe area spacer */}
        <div className="h-[env(safe-area-inset-bottom)]" />
      </div>
    </div>
  );
}
